import type { ComponentProps, ReactNode } from 'react';
import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

import { nanoid } from 'nanoid/non-secure';

import { NoticeBannersStackContext } from './NoticeBannersStackContext';

import type { NoticeBanner } from '../NoticeBanner';

//
// ─── CONTEXT ────────────────────────────────────────────────────────────────────
//

/**
 * The actions live in their own context so components that only dispatch
 * banners are not re-rendered every time the stack changes.
 */
const NoticeBannersStackActionsContext = createContext<
  NoticeBannersStackActionsContextValue | undefined
>(undefined);

export const NoticeBannersStackProvider = (props: NoticeBannersStackProviderProps) => {
  const { children, initialBanners = [], variant = 'banner', isLiquidGlassEnabled = false } = props;

  const [isEnabled, setIsEnabled] = useState(true);
  const [stack, setStack] = useState<NoticeBannersStackItems>(initialBanners);

  // ────────────────────────────────────────────────────────────────────

  const push = useCallback<NoticeBannersStackActionsContextValue['push']>(
    (banner, preventDuplicates) => {
      if (!isEnabled) return;

      setStack((currentStack) => {
        const bannerAlreadyExists =
          preventDuplicates && checkIfBannerAlreadyExists(currentStack, banner.id);

        if (bannerAlreadyExists) return currentStack;

        // An explicit `id: undefined` must not win, toasts are removed by id. A fresh `instanceId`
        // makes a toast re-pushed under the same id mount again with its own timer.
        return [{ ...banner, id: banner.id ?? nanoid(), instanceId: nanoid() }, ...currentStack];
      });
    },
    [isEnabled],
  );

  const pop = useCallback<NoticeBannersStackActionsContextValue['pop']>(() => {
    setStack((currentStack) => currentStack.slice(1));
  }, []);

  const clear = useCallback<NoticeBannersStackActionsContextValue['clear']>(() => {
    setStack([]);
  }, []);

  const remove = useCallback<NoticeBannersStackActionsContextValue['remove']>((id: string) => {
    setStack((currentStack) => currentStack.filter((banner) => banner.id !== id));
  }, []);

  const enable = useCallback<NoticeBannersStackActionsContextValue['enable']>(() => {
    setIsEnabled(true);
  }, []);

  const disable = useCallback<NoticeBannersStackActionsContextValue['disable']>(() => {
    setIsEnabled(false);
  }, []);

  // ────────────────────────────────────────────────────────────────────

  const actionsValue = useMemo(
    () => ({ push, pop, remove, clear, enable, disable }),
    [push, pop, remove, clear, enable, disable],
  );

  const stackValue = useMemo(
    () => ({ stack, variant, isLiquidGlassEnabled }),
    [stack, variant, isLiquidGlassEnabled],
  );

  return (
    <NoticeBannersStackActionsContext.Provider value={actionsValue}>
      <NoticeBannersStackContext.Provider value={stackValue}>
        {children}
      </NoticeBannersStackContext.Provider>
    </NoticeBannersStackActionsContext.Provider>
  );
};

// ─── HOOKS ──────────────────────────────────────────────────────────────────────

export const useNoticeBannersStackActions = () => {
  const context = useContext(NoticeBannersStackActionsContext);

  if (context === undefined) {
    throw new Error(
      'useNoticeBannersStackActions must be used within a <NoticeBannersStackProvider>',
    );
  }

  return context;
};

// ─── UTILS ──────────────────────────────────────────────────────────────────────

const checkIfBannerAlreadyExists = (currentStack: NoticeBannersStackItems, bannerId = '') => {
  return currentStack.some((stackBanner) => stackBanner.id === bannerId);
};

// ─── TYPES ──────────────────────────────────────────────────────────────────────

type NoticeBannersStackProviderProps = Readonly<{
  children: ReactNode;
  initialBanners?: NoticeBannersStackItems;
  variant?: NoticeBannersStackVariant;
  isLiquidGlassEnabled?: boolean;
}>;

type NoticeBannersStackActionsContextValue = Readonly<{
  push: (banner: NoticeBannerStackItem, preventDuplicates?: boolean) => void;
  pop: () => void;
  remove: (id: string) => void;
  clear: () => void;
  disable: () => void;
  enable: () => void;
}>;

type NoticeBannerStackItem = Readonly<{
  targetId?: string;
  id?: string;

  /**
   * Keeps this notice as a banner while the provider shows toasts.
   * Has no effect when the provider shows banners.
   */
  variant?: NoticeBannersStackVariant;
}> &
  NoticeBannersStackItemProps;

type NoticeBannersStackVariant = 'banner' | 'toast';

type NoticeBannersStackItemProps = ComponentProps<typeof NoticeBanner>;

type NoticeBannersStackStoredItem = NoticeBannerStackItem & Readonly<{ instanceId?: string }>;

export type NoticeBannersStackItems = readonly NoticeBannersStackStoredItem[];
